import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { ProductConsolidationAPI } from '../services/productConsolidation';
import { ServiceCostCentersAPI } from '../services/serviceCostCenters';
import QuotationRequestsPanel from './QuotationRequestsPanel';

const PROCESS_STATUS_LABELS = {
  NAO_ANALISADA: 'Não analisada',
  EM_ANALISE: 'Em análise',
  NECESSITA_COTACAO: 'Necessita cotação',
  AGUARDANDO_FORNECEDORES: 'Aguardando fornecedores',
  COTACOES_RECEBIDAS: 'Cotações recebidas',
  CUSTOS_SELECIONADOS: 'Custos selecionados',
  PRONTA_PARA_ATUALIZACAO: 'Pronta para atualização',
  ATUALIZADA_NO_BLING: 'Atualizada no Bling',
  CONFLITO: 'Conflito',
  ERRO_DE_SINCRONIZACAO: 'Erro de sincronização'
};

function formatValue(value, fallback = 'Não informado') {
  return value === null || value === undefined || String(value).trim() === '' ? fallback : String(value);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatNumber(value) {
  return numericValue(value).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function numericValue(value) {
  if (value === null || value === undefined || value === '') return 0;
  const raw = String(value).trim();
  const normalized = raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
}

function quoteHasPositiveValue(quote = {}) {
  return numericValue(quote.valorUnitario ?? quote.valor ?? quote.precoUnitario) > 0;
}

function calculateSaleValue(cost, marginPercent = 0, bdiPercent = 0) {
  const saleValue = numericValue(cost) * (1 + numericValue(marginPercent) / 100) * (1 + numericValue(bdiPercent) / 100);
  return Number(saleValue.toFixed(2));
}

function selectedProposalValue(item = {}, selectedQuote = {}, operation = 'recalcular', pricing = {}) {
  if (!selectedQuote || !quoteHasPositiveValue(selectedQuote)) return null;
  if (operation === 'somente_custo') return undefined;
  if (operation === 'manter_preco') return numericValue(item.precoVendaAtual);
  return calculateSaleValue(selectedQuote.valorUnitario, pricing.marginPercent, pricing.bdiPercent);
}

function previewQuantity(item = {}) {
  return numericValue(item.quantidadeProposta ?? item.quantidade ?? item.quantity) || 1;
}

function previewOriginalUnitPrice(item = {}) {
  return numericValue(item.precoVendaAtual ?? item.precoAnterior ?? item.valorUnitarioAtual);
}

function previewFinalUnitPrice(previewItem = {}, analysisItem = {}) {
  if (previewItem.precoVenda === undefined) return previewOriginalUnitPrice(analysisItem);
  return numericValue(previewItem.precoVenda);
}

function formatDate(value) {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Não informado' : date.toLocaleDateString('pt-BR');
}

function quoteKey(itemId, quote = {}) {
  return `${itemId}:${quote.itemPedidoCompraId || quote.pedidoCompraIdBling || quote.numeroPedidoCompra || quote.valorUnitario}`;
}

function quoteSelectionKey(itemId, quote = {}, index = 0) {
  return `${quoteKey(itemId, quote)}:${index}`;
}

function storageKey(proposalId) {
  return `taskhub:proposal-cost-selection:${proposalId}`;
}

function processLabel(status) {
  return PROCESS_STATUS_LABELS[status] || formatValue(status, 'Não analisada');
}

function supplierKey(supplier = {}) {
  if (typeof supplier === 'string' || typeof supplier === 'number') return String(supplier).trim();
  return String(
    supplier.id ||
    supplier.fornecedorIdBling ||
    supplier.fornecedor?.id ||
    supplier.contato?.id ||
    supplier.externalId ||
    supplier.blingContatoId ||
    supplier.metadata?.blingContatoId ||
    supplier.rawData?.id ||
    supplier.raw?.id ||
    ''
  ).trim();
}

function supplierName(supplier = {}) {
  if (typeof supplier === 'string' || typeof supplier === 'number') return '';
  return supplier.nome || supplier.label || supplier.fornecedorNome || supplier.fornecedor?.nome || supplier.contato?.nome || '';
}

function productKey(product = {}) {
  return String(product.id || product._id || product.refs?.cecIntegratedId || product.refs?.blingProdutoId || product.sku || product.name || '').trim();
}

function productName(product = {}) {
  return product.name || product.nome || product.description || product.descricao || '';
}

function isServiceProduct(product = {}) {
  const fields = [product.type, product.tipo, product.category, product.categoria, productName(product), product.sku, product.code].join(' ').toLowerCase();
  return fields.includes('servico') || fields.includes('serviço') || fields.includes('mao de obra') || fields.includes('mão de obra');
}

function quoteAgeNumber(quote = {}) {
  const age = Number(quote.idadeEmDias);
  return Number.isFinite(age) ? age : null;
}

function quoteStatusClass(quote = {}, maxAgeDays = '') {
  const status = String(quote.situacaoValidade || '').toUpperCase();
  const age = quoteAgeNumber(quote);
  const maxAge = Number(maxAgeDays);
  if (status === 'VENCIDA') return 'expired';
  if (Number.isFinite(maxAge) && maxAge > 0 && age !== null && age >= maxAge * 0.75) return 'aging';
  if (status === 'ATUAL') return 'current';
  return 'unknown';
}

function normalizeUpdateResult(payload = {}) {
  const nestedResponse = payload.payload?.response;
  const response = payload.response || nestedResponse || payload;
  return {
    ...response,
    error: response.error || payload.error,
    code: response.code || payload.code,
    conflitos: response.conflitos || payload.conflitos || payload.payload?.conflitos || []
  };
}

export function buildSelectedUpdateItems(items = [], selectedQuotes = {}, operations = {}) {
  return items
    .map((item) => {
      const selected = selectedQuotes[item.itemPropostaId];
      if (!selected) return null;
      return {
        itemPropostaId: item.itemPropostaId,
        itemPedidoCompraId: selected.itemPedidoCompraId,
        novoCusto: selected.valorUnitario,
        valorUnitario: selected.valorUnitario,
        custoAtual: item.custoAtual,
        precoVendaAtual: item.precoVendaAtual,
        cotacaoSelecionada: selected,
        operacao: operations[item.itemPropostaId] || 'recalcular'
      };
    })
    .filter(Boolean);
}

export function buildQuoteOrderPayload({ proposalId, items = [], suppliers = [], observations = '', returnDate = '' }) {
  return {
    propostaId: proposalId,
    fornecedores: suppliers
      .map((supplier) => {
        const supplierId = supplierKey(supplier);
        if (!supplierId) return null;
        const name = supplierName(supplier);
        return {
          fornecedorIdBling: supplierId,
          fornecedor: { id: supplierId, ...(name ? { nome: name } : {}) },
          contato: { id: supplierId, ...(name ? { nome: name } : {}) },
          fornecedorNome: name,
          itens: items.map((item) => ({
            itemPropostaId: item.itemPropostaId,
            produtoIdBling: item.produtoIdBling,
            codigo: item.codigo,
            descricao: item.descricao,
            unidade: item.unidade,
            quantidade: item.quantidadeProposta,
            valor: 0
          }))
        };
      })
      .filter(Boolean),
    observacoes: observations,
    prazoRetorno: returnDate
  };
}

export default function ProposalCostUpdateFlow({ onUnauthorized }) {
  const [showQuotationRequests, setShowQuotationRequests] = useState(false);
  const [filters, setFilters] = useState({ number: '', customer: '', dateStart: '', dateEnd: '', status: '' });
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [selectedQuotes, setSelectedQuotes] = useState({});
  const [operations, setOperations] = useState({});
  const [itemsForQuote, setItemsForQuote] = useState({});
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [whatsappText, setWhatsappText] = useState('');
  const [copyState, setCopyState] = useState('');
  const [quoteOrderResult, setQuoteOrderResult] = useState(null);
  const [manualQuoteOrderNumber, setManualQuoteOrderNumber] = useState('');
  const [pricing, setPricing] = useState({ marginPercent: 20, bdiPercent: 0 });
  const [laborSearch, setLaborSearch] = useState('');
  const [laborOptions, setLaborOptions] = useState([]);
  const [selectedLaborItems, setSelectedLaborItems] = useState([]);
  const [quoteMaxAgeDays, setQuoteMaxAgeDays] = useState('');
  const [preview, setPreview] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [purchasePreparation, setPurchasePreparation] = useState(null);
  const [purchaseSelections, setPurchaseSelections] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [audit, setAudit] = useState(null);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const proposalId = selectedProposal?.id;
  const analysisItems = useMemo(() => analysis?.itens || [], [analysis]);
  const analysisItemsById = useMemo(
    () => new Map(analysisItems.map((item) => [String(item.itemPropostaId), item])),
    [analysisItems]
  );
  const quoteIsVisible = useCallback((quote) => {
    if (!quoteHasPositiveValue(quote)) return false;
    const maxAge = Number(quoteMaxAgeDays);
    if (!Number.isFinite(maxAge) || maxAge <= 0) return true;
    const age = quoteAgeNumber(quote);
    return age === null || age <= maxAge;
  }, [quoteMaxAgeDays]);
  const visibleSelectedQuotes = useMemo(() => {
    const entries = Object.entries(selectedQuotes).filter(([, quote]) => quoteIsVisible(quote));
    return Object.fromEntries(entries);
  }, [quoteIsVisible, selectedQuotes]);
  const selectedUpdateItems = useMemo(
    () => buildSelectedUpdateItems(analysisItems, visibleSelectedQuotes, operations),
    [analysisItems, visibleSelectedQuotes, operations]
  );
  const suppliers = selectedSuppliers.filter((supplier) => supplierKey(supplier));
  const selectedNewQuoteItems = analysisItems.filter((item) => itemsForQuote[item.itemPropostaId]);
  const updateConflicts = updateResult?.conflitos || [];
  const previewTotals = useMemo(() => {
    const materialQuantity = (preview?.itens || []).reduce((sum, item) => {
      const analysisItem = analysisItemsById.get(String(item.itemPropostaId)) || {};
      return sum + previewQuantity(analysisItem);
    }, 0);
    const materialOriginalSale = (preview?.itens || []).reduce((sum, item) => {
      const analysisItem = analysisItemsById.get(String(item.itemPropostaId)) || {};
      return sum + (previewQuantity(analysisItem) * previewOriginalUnitPrice(analysisItem));
    }, 0);
    const materialCost = (preview?.itens || []).reduce((sum, item) => {
      const analysisItem = analysisItemsById.get(String(item.itemPropostaId)) || {};
      return sum + (previewQuantity(analysisItem) * numericValue(item.novoCusto));
    }, 0);
    const materialSale = (preview?.itens || []).reduce((sum, item) => {
      const analysisItem = analysisItemsById.get(String(item.itemPropostaId)) || {};
      return sum + (previewQuantity(analysisItem) * previewFinalUnitPrice(item, analysisItem));
    }, 0);
    const laborSale = selectedLaborItems.reduce((sum, item) => sum + numericValue(item.salePrice), 0);
    return {
      materialQuantity,
      materialOriginalSale,
      materialCost,
      materialAverageUnitCost: materialQuantity ? materialCost / materialQuantity : 0,
      materialSale,
      laborSale,
      totalSale: materialSale + laborSale,
      itemCount: (preview?.itens || []).length,
      laborCount: selectedLaborItems.length
    };
  }, [analysisItemsById, preview, selectedLaborItems]);

  useEffect(() => {
    if (!proposalId) return;
    const stored = window.localStorage.getItem(storageKey(proposalId));
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      setSelectedQuotes(parsed.selectedQuotes || {});
      setOperations(parsed.operations || {});
      setItemsForQuote(parsed.itemsForQuote || {});
    } catch {
      // Ignore corrupted local state.
    }
  }, [proposalId]);

  useEffect(() => {
    if (!proposalId) return;
    window.localStorage.setItem(storageKey(proposalId), JSON.stringify({ selectedQuotes, operations, itemsForQuote }));
  }, [proposalId, selectedQuotes, operations, itemsForQuote]);

  function handleError(requestError, fallback) {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getErrorMessage(requestError, fallback));
  }

  useEffect(() => {
    if (!selectedProposal?.id) return;
    let cancelled = false;

    async function loadInitialSuppliers() {
      setSupplierLoading(true);
      try {
        const payload = await ServiceCostCentersAPI.proposalCostSuppliers({ search: '', limit: 80 });
        if (!cancelled) setSupplierOptions(payload.rows || []);
      } catch (requestError) {
        if (cancelled) return;
        if (isUnauthorizedError(requestError)) {
          onUnauthorized?.();
          return;
        }
        setError(getErrorMessage(requestError, 'Não foi possível carregar fornecedores do Bling.'));
      } finally {
        if (!cancelled) setSupplierLoading(false);
      }
    }

    loadInitialSuppliers();
    return () => {
      cancelled = true;
    };
  }, [selectedProposal?.id, onUnauthorized]);

  async function loadSuppliers(search = supplierSearch) {
    setSupplierLoading(true);
    try {
      const payload = await ServiceCostCentersAPI.proposalCostSuppliers({ search, limit: 80 });
      setSupplierOptions(payload.rows || []);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível carregar fornecedores do Bling.');
    } finally {
      setSupplierLoading(false);
    }
  }

  function toggleSupplier(supplier) {
    const id = supplierKey(supplier);
    if (!id) return;
    setSelectedSuppliers((current) => {
      if (current.some((item) => supplierKey(item) === id)) {
        return current.filter((item) => supplierKey(item) !== id);
      }
      return [...current, supplier];
    });
  }

  function removeSupplier(id) {
    setSelectedSuppliers((current) => current.filter((item) => supplierKey(item) !== id));
  }

  function toggleLaborItem(product) {
    const key = productKey(product);
    if (!key) return;
    setSelectedLaborItems((current) => (
      current.some((item) => productKey(item) === key)
        ? current.filter((item) => productKey(item) !== key)
        : [...current, product]
    ));
  }

  function supplierLabelById(id) {
    const supplier = selectedSuppliers.find((item) => supplierKey(item) === String(id));
    return supplier?.nome || supplier?.label || `Fornecedor ${formatValue(id)}`;
  }

  async function loadProposals(event) {
    event?.preventDefault();
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;
    setActionId('proposals');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.proposalCostProposals({ number: filters.number, search: filters.number }, { signal: controller.signal });
      setProposals(payload.rows || []);
      if (payload.pending) setError(payload.pending);
      if (!payload.rows?.length && filters.number) setError('Nenhuma proposta sincronizada encontrada para este número.');
    } catch (requestError) {
      if (requestError?.name !== 'CanceledError') handleError(requestError, 'Não foi possível listar propostas sincronizadas.');
    } finally {
      setActionId('');
    }
  }

  async function syncProposal() {
    if (!filters.number.trim() || actionId) return;
    setActionId('sync-proposal');
    setError('');
    try {
      await ServiceCostCentersAPI.syncProposalCostProposal({ number: filters.number.trim() });
      await loadProposals();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível sincronizar a proposta pelo integrador.');
    } finally {
      setActionId('');
    }
  }

  async function searchLaborProducts(event) {
    event?.preventDefault();
    const query = laborSearch.trim() || 'servico';
    setActionId('labor-search');
    setError('');
    try {
      const payload = await ProductConsolidationAPI.search(query);
      const products = payload?.masterProducts || [];
      setLaborOptions(products.filter(isServiceProduct));
    } catch (requestError) {
      handleError(requestError, 'Não foi possível buscar produtos de serviço/mão de obra.');
    } finally {
      setActionId('');
    }
  }

  async function openProposal(proposal) {
    setSelectedProposal(proposal);
    setAnalysis(null);
    setPreview(null);
    setUpdateResult(null);
    setPurchasePreparation(null);
    setPurchaseSelections({});
    setPurchaseOrders([]);
    setPurchaseResult(null);
    setAudit(null);
    setManualQuoteOrderNumber('');
    setSelectedSuppliers([]);
    setSupplierSearch('');
    setActionId(`analysis-${proposal.id}`);
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.proposalCostAnalysis(proposal.id);
      setAnalysis(payload);
      setQuoteMaxAgeDays(String(payload.validadeEmDias || 60));
    } catch (requestError) {
      handleError(requestError, 'Não foi possível carregar o histórico de cotações da proposta.');
    } finally {
      setActionId('');
    }
  }

  async function openStockCheckReport(proposal) {
    if (!proposal?.id || actionId) return;
    const reportWindow = window.open('about:blank', '_blank');
    if (!reportWindow) {
      setError('O navegador bloqueou a abertura do relatório. Libere pop-ups e tente novamente.');
      return;
    }
    reportWindow.document.write('<!doctype html><title>Gerando relatório</title><body style="font-family: Arial, sans-serif; padding: 24px;">Gerando relatório de checagem de estoque...</body>');
    reportWindow.document.close();
    setActionId(`stock-report-${proposal.id}`);
    setError('');
    try {
      const blob = await ServiceCostCentersAPI.proposalStockCheckReport(proposal.id, {
        numero: proposal.numero,
        cliente: proposal.cliente,
        data: proposal.data
      });
      const reportUrl = URL.createObjectURL(blob);
      reportWindow.location.replace(reportUrl);
    } catch (requestError) {
      reportWindow.document.open();
      reportWindow.document.write('<!doctype html><title>Erro</title><body style="font-family: Arial, sans-serif; padding: 24px;">Não foi possível gerar o relatório de estoque.</body>');
      reportWindow.document.close();
      handleError(requestError, 'Não foi possível gerar o relatório de estoque.');
    } finally {
      setActionId('');
    }
  }

  function chooseQuote(item, quote, selectionKey) {
    const nextQuote = { ...quote, confirmarCotacaoVencida: quote.situacaoValidade === 'VENCIDA', selectionKey };
    setSelectedQuotes((current) => ({ ...current, [item.itemPropostaId]: nextQuote }));
    setOperations((current) => ({ ...current, [item.itemPropostaId]: current[item.itemPropostaId] || 'recalcular' }));
  }

  async function generateWhatsappList() {
    setActionId('whatsapp');
    setCopyState('');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.proposalWhatsappList({ items: selectedNewQuoteItems, observacao: quoteNotes });
      setWhatsappText(payload.message || '');
    } catch (requestError) {
      handleError(requestError, 'Não foi possível gerar a lista para WhatsApp.');
    } finally {
      setActionId('');
    }
  }

  async function copyWhatsappList() {
    await navigator.clipboard.writeText(whatsappText);
    setCopyState('Texto copiado.');
  }

  async function requestQuoteOrders() {
    if (!proposalId || !selectedNewQuoteItems.length || !suppliers.length || actionId) return;
    setActionId('quote-orders');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.createProposalQuoteOrders(buildQuoteOrderPayload({
        proposalId,
        items: selectedNewQuoteItems,
        suppliers,
        observations: quoteNotes,
        returnDate
      }));
      setQuoteOrderResult(payload.response || payload);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível solicitar pedidos de cotação ao integrador.');
    } finally {
      setActionId('');
    }
  }

  async function refreshQuoteOrder(number) {
    const safeNumber = String(number || '').trim();
    if (!safeNumber || actionId) return;
    setActionId(`sync-order-${safeNumber}`);
    setError('');
    try {
      await ServiceCostCentersAPI.syncProposalQuoteOrder({ number: safeNumber });
      if (safeNumber === manualQuoteOrderNumber.trim()) setManualQuoteOrderNumber('');
      if (selectedProposal) await openProposal(selectedProposal);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível sincronizar o pedido de compra.');
    } finally {
      setActionId('');
    }
  }

  async function loadPreview() {
    if (!selectedUpdateItems.length || actionId) return;
    setActionId('preview-cost-update');
    setError('');
    setUpdateResult(null);
    try {
      const payload = await ServiceCostCentersAPI.previewProposalCostUpdate(proposalId, { items: selectedUpdateItems, pricing });
      setPreview(payload);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível gerar a prévia da atualização.');
    } finally {
      setActionId('');
    }
  }

  async function confirmUpdate(confirmarConflitos = false) {
    if (!preview || actionId) return;
    setActionId('confirm-update');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.confirmProposalCostUpdate(proposalId, {
        items: selectedUpdateItems,
        pricing,
        confirmarConflitos
      });
      setUpdateResult(normalizeUpdateResult(payload));
      await loadAudit();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível atualizar a proposta. Verifique conflitos e tente novamente.');
      if (requestError?.response?.status === 409) setUpdateResult(normalizeUpdateResult(requestError.response.data));
    } finally {
      setActionId('');
    }
  }

  async function loadAudit() {
    if (!proposalId) return;
    setActionId('audit');
    try {
      const payload = await ServiceCostCentersAPI.proposalCostAudit(proposalId, { limit: 100 });
      setAudit(payload);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível consultar auditoria.');
    } finally {
      setActionId('');
    }
  }

  async function loadPurchasePreparation() {
    if (!proposalId || actionId) return;
    setActionId('purchase-preparation');
    setError('');
    try {
      const [preparationPayload, ordersPayload] = await Promise.all([
        ServiceCostCentersAPI.proposalPurchasePreparation(proposalId),
        ServiceCostCentersAPI.proposalPurchaseOrders(proposalId)
      ]);
      const mergedPreparation = {
        ...preparationPayload,
        items: (preparationPayload.items || []).map((item) => {
          const selectedQuote = selectedQuotes[item.proposalItemId];
          if (!selectedQuote) return item;
          const supplierId = selectedQuote.fornecedor?.id || selectedQuote.fornecedorIdBling || selectedQuote.supplierId || item.proposalSupplierId;
          const supplierName = selectedQuote.fornecedor?.nome || selectedQuote.fornecedorNome || selectedQuote.supplierName || item.proposalSupplierName;
          return {
            ...item,
            proposalCostUnitValue: numericValue(selectedQuote.valorUnitario, item.proposalCostUnitValue),
            proposalSupplierId: supplierId,
            proposalSupplierName: supplierName,
            selectedQuoteReferenceId: selectedQuote.itemPedidoCompraId || item.selectedQuoteReferenceId,
            selectedQuote,
            warnings: (item.warnings || []).filter((warning) => warning !== 'Item sem custo selecionado na proposta.')
          };
        })
      };
      setPurchasePreparation(mergedPreparation);
      const nextSelections = {};
      (mergedPreparation.items || []).forEach((item) => {
        const fallbackQuote = item.selectedQuote || item.availableQuotes?.[0] || {};
        const supplierId = item.proposalSupplierId || fallbackQuote.fornecedor?.id || fallbackQuote.fornecedorIdBling || '';
        nextSelections[item.proposalItemId] = {
          supplierBlingId: supplierId,
          supplierName: item.proposalSupplierName || fallbackQuote.fornecedor?.nome || fallbackQuote.fornecedorNome || '',
          quoteItemReferenceId: item.selectedQuoteReferenceId || fallbackQuote.itemPedidoCompraId || '',
          quantity: item.approvedQuantity || 1,
          purchaseUnitValue: item.proposalCostUnitValue || numericValue(fallbackQuote.valorUnitario)
        };
      });
      setPurchaseSelections(nextSelections);
      setPurchaseOrders(ordersPayload.orders || []);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível preparar os pedidos de compra.');
    } finally {
      setActionId('');
    }
  }

  async function generatePurchaseOrders() {
    if (!proposalId || actionId) return;
    const sourceItems = purchasePreparation?.items || [];
    const items = sourceItems
      .map((item) => {
        const selection = purchaseSelections[item.proposalItemId] || {};
        const selectedQuote = (item.availableQuotes || []).find((quote) => {
          const quoteSupplierId = quote.fornecedor?.id || quote.fornecedorIdBling || quote.supplierId;
          return String(quoteSupplierId || '') === String(selection.supplierBlingId || '');
        }) || item.selectedQuote || {};
        if (!selection.supplierBlingId) return null;
        return {
          proposalItemId: item.proposalItemId,
          supplierBlingId: selection.supplierBlingId,
          supplierName: selection.supplierName || selectedQuote.fornecedor?.nome || selectedQuote.fornecedorNome || selectedQuote.supplierName,
          productBlingId: item.productBlingId || selectedQuote.produtoIdBling,
          quoteItemReferenceId: selection.quoteItemReferenceId || selectedQuote.itemPedidoCompraId,
          description: item.description || selectedQuote.descricao,
          unit: item.unit || selectedQuote.unidade,
          quantity: selection.quantity,
          proposalCostUnitValue: item.proposalCostUnitValue,
          purchaseUnitValue: selection.purchaseUnitValue
        };
      })
      .filter(Boolean);
    if (!items.length) {
      setError('Selecione ao menos um fornecedor para gerar os pedidos de compra.');
      return;
    }
    setActionId('generate-purchase-orders');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.generateProposalPurchaseOrders(proposalId, {
        proposalNumber: selectedProposal?.numero,
        version: 'atual',
        items
      });
      setPurchaseResult(payload);
      setPurchaseOrders(payload.orders || []);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível gerar os pedidos de compra efetivos.');
    } finally {
      setActionId('');
    }
  }

  async function sendPurchaseOrder(order) {
    const id = order?._id || order?.id;
    if (!id || actionId) return;
    setActionId(`send-purchase-${id}`);
    setError('');
    try {
      const payload = order.status === 'ERRO_DE_INTEGRACAO'
        ? await ServiceCostCentersAPI.reprocessProposalPurchaseOrder(id)
        : await ServiceCostCentersAPI.sendProposalPurchaseOrder(id);
      setPurchaseResult(payload);
      await loadPurchasePreparation();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível enviar o pedido ao Bling.');
    } finally {
      setActionId('');
    }
  }

  function updatePurchaseSelection(itemId, patch) {
    setPurchaseSelections((current) => ({
      ...current,
      [itemId]: {
        ...(current[itemId] || {}),
        ...patch
      }
    }));
  }

  return (
    <section className="proposal-cost-flow">
      <form className="client-filter-bar mt-4" onSubmit={loadProposals}>
        <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-number">Número da proposta</label><input id="proposal-cost-number" className="form-control" value={filters.number} onChange={(event) => setFilters((current) => ({ ...current, number: event.target.value }))} placeholder="Ex.: 1542" /></div>
        <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-customer">Cliente</label><input id="proposal-cost-customer" className="form-control" value={filters.customer} onChange={(event) => setFilters((current) => ({ ...current, customer: event.target.value }))} /></div>
        <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-date-start">De</label><input id="proposal-cost-date-start" className="form-control" type="date" value={filters.dateStart} onChange={(event) => setFilters((current) => ({ ...current, dateStart: event.target.value }))} /></div>
        <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-date-end">Até</label><input id="proposal-cost-date-end" className="form-control" type="date" value={filters.dateEnd} onChange={(event) => setFilters((current) => ({ ...current, dateEnd: event.target.value }))} /></div>
        <button type="submit" className="btn btn-dark" disabled={actionId === 'proposals'}>{actionId === 'proposals' ? 'Buscando...' : 'Listar propostas'}</button>
        <button type="button" className="btn btn-outline-primary" disabled={!filters.number.trim() || actionId === 'sync-proposal'} onClick={syncProposal}>{actionId === 'sync-proposal' ? 'Sincronizando...' : 'Sincronizar número'}</button>
      </form>

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

      <div className="client-table-wrap mt-3">
        <table className="client-audit-table">
          <thead><tr><th>Número</th><th>Cliente</th><th>Data</th><th>Última sincronização</th><th>Situação comercial</th><th>Processo</th><th></th></tr></thead>
          <tbody>
            {!proposals.length && <tr><td colSpan="7">Informe um número e sincronize a proposta criada no Bling.</td></tr>}
            {proposals.map((proposal) => (
              <tr key={proposal.id}>
                <td>{formatValue(proposal.numero)}</td>
                <td>{formatValue(proposal.cliente)}</td>
                <td>{formatDate(proposal.data)}</td>
                <td>{formatDate(proposal.ultimaSincronizacao)}</td>
                <td>{formatValue(proposal.situacaoComercial)}</td>
                <td>{processLabel(proposal.situacaoCotacao)}</td>
                <td>
                  <button type="button" className="btn btn-sm btn-primary me-2" disabled={actionId === `analysis-${proposal.id}`} onClick={() => openProposal(proposal)}>Abrir análise</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" disabled={actionId === `stock-report-${proposal.id}`} onClick={() => openStockCheckReport(proposal)}>
                    {actionId === `stock-report-${proposal.id}` ? 'Gerando...' : 'Relatório estoque'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProposal && (
        <div className="client-detail-section mt-3">
          <div className="service-workspace-heading">
            <div>
              <div className="eyebrow">Análise de custos</div>
              <h3>Proposta {formatValue(selectedProposal.numero || selectedProposal.id)}</h3>
              <p className="text-muted mb-0">{formatValue(selectedProposal.cliente)} · {processLabel(selectedProposal.situacaoCotacao)}</p>
            </div>
            <div className="overlay-actions">
              <div className="quote-age-filter">
                <label className="form-label" htmlFor="proposal-cost-quote-max-age">Máx. dias</label>
                <input
                  id="proposal-cost-quote-max-age"
                  className="form-control form-control-sm"
                  type="number"
                  min="0"
                  value={quoteMaxAgeDays}
                  onChange={(event) => setQuoteMaxAgeDays(event.target.value)}
                />
              </div>
              <div className="quote-age-filter">
                <label className="form-label" htmlFor="proposal-cost-sync-order-number">Pedido compra</label>
                <input
                  id="proposal-cost-sync-order-number"
                  className="form-control form-control-sm"
                  value={manualQuoteOrderNumber}
                  onChange={(event) => setManualQuoteOrderNumber(event.target.value)}
                  placeholder="Número"
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={!manualQuoteOrderNumber.trim() || actionId === `sync-order-${manualQuoteOrderNumber.trim()}`}
                onClick={() => refreshQuoteOrder(manualQuoteOrderNumber)}
              >
                {actionId === `sync-order-${manualQuoteOrderNumber.trim()}` ? 'Sincronizando...' : 'Sincronizar pedido'}
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openProposal(selectedProposal)}>Atualizar cotações</button>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowQuotationRequests((value) => !value)}>{showQuotationRequests ? 'Fechar solicitações' : 'Gerar solicitação de cotação'}</button>
              <button type="button" className="btn btn-sm btn-outline-success" disabled={actionId === 'purchase-preparation'} onClick={loadPurchasePreparation}>
                {actionId === 'purchase-preparation' ? 'Preparando...' : 'Preparar compras'}
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadAudit}>Auditoria</button>
            </div>
          </div>

          {showQuotationRequests && <QuotationRequestsPanel key={selectedProposal.id} proposal={selectedProposal} onUnauthorized={onUnauthorized} />}
          <div className="client-table-wrap mt-3">
            <table className="client-audit-table proposal-cost-table">
              <thead><tr><th>Cotar</th><th>Item</th><th>Qtd.</th><th>Custo atual</th><th>Valor atual proposta</th><th>Valor após seleção</th><th>Cotações</th><th>Operação</th></tr></thead>
              <tbody>
                {!analysisItems.length && <tr><td colSpan="8">Carregando ou sem itens de proposta.</td></tr>}
                {analysisItems.map((item) => {
                  const itemQuotes = item.cotacoes || [];
                  const pricedQuotes = itemQuotes.filter(quoteHasPositiveValue);
                  const visibleQuotes = itemQuotes
                    .map((quote, index) => ({ quote, index }))
                    .filter(({ quote }) => quoteIsVisible(quote));
                  const selectedQuote = selectedQuotes[item.itemPropostaId];
                  const operation = operations[item.itemPropostaId] || 'recalcular';
                  const nextProposalValue = selectedProposalValue(item, selectedQuote, operation, pricing);
                  return (
                    <tr key={item.itemPropostaId}>
                      <td><input type="checkbox" checked={Boolean(itemsForQuote[item.itemPropostaId])} onChange={(event) => setItemsForQuote((current) => ({ ...current, [item.itemPropostaId]: event.target.checked }))} /></td>
                      <td><strong>{formatValue(item.codigo)}</strong><br />{formatValue(item.descricao)}<br /><span className="text-muted">{formatValue(item.unidade)} · {formatValue(item.produtoIdBling, 'Sem ID Bling')}</span></td>
                      <td>{formatValue(item.quantidadeProposta)}</td>
                      <td>{formatMoney(item.custoAtual)}</td>
                      <td>{formatMoney(item.precoVendaAtual)}</td>
                      <td>
                        {nextProposalValue === null && <span className="text-muted">Selecione cotação</span>}
                        {nextProposalValue === undefined && <span className="text-muted">Sem alteração no Bling</span>}
                        {nextProposalValue !== null && nextProposalValue !== undefined && <strong>{formatMoney(nextProposalValue)}</strong>}
                      </td>
                      <td>
                        <div className="proposal-quote-list">
                          {!pricedQuotes.length && <span className="badge text-bg-warning">Sem histórico</span>}
                          {!!pricedQuotes.length && !visibleQuotes.length && <span className="badge text-bg-warning">Sem cotações no período</span>}
                          {visibleQuotes.map(({ quote, index }) => {
                          const currentSelectionKey = quoteSelectionKey(item.itemPropostaId, quote, index);
                          const selected = selectedQuote?.selectionKey
                            ? selectedQuote.selectionKey === currentSelectionKey
                            : quoteKey(item.itemPropostaId, selectedQuote) === quoteKey(item.itemPropostaId, quote);
                          return (
                            <div
                              key={currentSelectionKey}
                              className={`proposal-quote-option ${quoteStatusClass(quote, quoteMaxAgeDays)} ${selected ? 'active' : ''}`}
                            >
                              <button
                                type="button"
                                className="proposal-quote-choice"
                                onClick={() => chooseQuote(item, quote, currentSelectionKey)}
                              >
                                <strong>{formatValue(quote.fornecedor?.nome || quote.fornecedorNome || quote.supplierName, `Fornecedor ${quote.fornecedor?.id || quote.fornecedorIdBling || ''}`)}</strong>
                                <span>{formatMoney(quote.valorUnitario)} · {formatDate(quote.data)} · Pedido {formatValue(quote.numeroPedidoCompra)}</span>
                                <small>{formatValue(quote.idadeEmDias, 'Sem idade')} dias · {formatValue(quote.situacaoValidade)} · {formatValue(quote.origemCorrespondencia)}</small>
                                {!!quote.alertas?.length && <em>{quote.alertas.join(' ')}</em>}
                              </button>
                              {quote.numeroPedidoCompra && (
                                <button
                                  type="button"
                                  className="proposal-quote-sync"
                                  onClick={() => refreshQuoteOrder(quote.numeroPedidoCompra)}
                                >
                                  Sincronizar pedido
                                </button>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      </td>
                      <td>
                        <select className="form-select form-select-sm" value={operation} onChange={(event) => setOperations((current) => ({ ...current, [item.itemPropostaId]: event.target.value }))}>
                          <option value="recalcular">Recalcular preço</option>
                          <option value="manter_preco">Manter preço atual</option>
                          <option value="somente_custo">Somente custo interno</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="proposal-cost-actions mt-3">
            <div className="filter-control supplier-picker">
              <label className="form-label" htmlFor="proposal-cost-supplier-search">Fornecedores Bling</label>
              <div className="supplier-picker__search">
                <input
                  id="proposal-cost-supplier-search"
                  className="form-control"
                  value={supplierSearch}
                  onChange={(event) => setSupplierSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      loadSuppliers();
                    }
                  }}
                  placeholder="Buscar por nome, CNPJ ou e-mail"
                />
                <button type="button" className="btn btn-outline-secondary btn-sm" disabled={supplierLoading} onClick={() => loadSuppliers()}>
                  {supplierLoading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
              <div className="supplier-picker__menu" role="listbox" aria-label="Fornecedores Bling">
                {supplierOptions.map((supplier) => {
                  const id = supplierKey(supplier);
                  const checked = selectedSuppliers.some((item) => supplierKey(item) === id);
                  return (
                    <label key={id} className={`supplier-picker__option ${checked ? 'active' : ''}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleSupplier(supplier)} />
                      <span><strong>{formatValue(supplier.nome || supplier.label)}</strong><small>ID {formatValue(id)}{supplier.documento ? ` · ${supplier.documento}` : ''}</small></span>
                    </label>
                  );
                })}
                {!supplierOptions.length && <span className="supplier-picker__empty">{supplierLoading ? 'Carregando fornecedores...' : 'Nenhum fornecedor encontrado.'}</span>}
              </div>
              {!!selectedSuppliers.length && (
                <div className="supplier-picker__selected">
                  {selectedSuppliers.map((supplier) => {
                    const id = supplierKey(supplier);
                    return <button key={id} type="button" onClick={() => removeSupplier(id)}>{formatValue(supplier.nome || supplier.label)} ×</button>;
                  })}
                </div>
              )}
            </div>
            <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-return-date">Prazo de retorno</label><input id="proposal-cost-return-date" className="form-control" type="date" value={returnDate} onChange={(event) => setReturnDate(event.target.value)} /></div>
            <div className="filter-control filter-control--wide"><label className="form-label" htmlFor="proposal-cost-notes">Observação</label><input id="proposal-cost-notes" className="form-control" value={quoteNotes} onChange={(event) => setQuoteNotes(event.target.value)} placeholder="Ex.: informar frete separado" /></div>
            <button type="button" className="btn btn-outline-primary" disabled={!selectedNewQuoteItems.length || actionId === 'whatsapp'} onClick={generateWhatsappList}>Gerar lista WhatsApp</button>
            <button type="button" className="btn btn-success" disabled={!selectedNewQuoteItems.length || !suppliers.length || actionId === 'quote-orders'} onClick={requestQuoteOrders}>{actionId === 'quote-orders' ? 'Solicitando...' : 'Solicitar pedidos'}</button>
          </div>

          {whatsappText && (
            <div className="client-detail-box mt-3">
              <h3>Lista para WhatsApp</h3>
              <textarea className="form-control quotation-message-preview" rows="8" value={whatsappText} onChange={(event) => setWhatsappText(event.target.value)} />
              <button type="button" className="btn btn-success mt-2" onClick={copyWhatsappList}>Copiar para WhatsApp</button>
              {copyState && <span className="text-success ms-2">{copyState}</span>}
            </div>
          )}

          {quoteOrderResult && (
            <div className="client-detail-box mt-3">
              <h3>Pedidos solicitados</h3>
              {(quoteOrderResult.resultados || []).map((row) => (
                <article key={`${row.fornecedorIdBling}-${row.pedidoCompraIdBling || row.error}`} className="client-linked-card">
                  <div>
                    <strong>{row.ok ? `Pedido ${formatValue(row.numero)}` : 'Falha na solicitação'}</strong>
                    <span>{supplierLabelById(row.fornecedorIdBling)} · Fornecedor Bling {formatValue(row.fornecedorIdBling)}</span>
                    <span>{row.ok ? `Pedido Bling ${formatValue(row.pedidoCompraIdBling)}` : formatValue(row.validationMessages?.join(' ') || row.detalhes || row.error, 'Pedido não criado')}</span>
                  </div>
                  {row.numero && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => refreshQuoteOrder(row.numero)}>Sincronizar pedido</button>}
                </article>
              ))}
            </div>
          )}

          <div className="client-detail-box mt-3">
            <h3>Preço de venda</h3>
            <div className="proposal-form-grid proposal-pricing-grid">
              <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-margin">Margem %</label><input id="proposal-cost-margin" className="form-control" type="number" value={pricing.marginPercent} onChange={(event) => setPricing((current) => ({ ...current, marginPercent: event.target.value }))} /></div>
              <div className="filter-control"><label className="form-label" htmlFor="proposal-cost-bdi">BDI %</label><input id="proposal-cost-bdi" className="form-control" type="number" value={pricing.bdiPercent} onChange={(event) => setPricing((current) => ({ ...current, bdiPercent: event.target.value }))} /></div>
              <button type="button" className="btn btn-primary" disabled={!selectedUpdateItems.length || actionId === 'preview-cost-update'} onClick={loadPreview}>Gerar prévia</button>
            </div>
          </div>

          <div className="client-detail-box mt-3">
            <h3>Mão de obra</h3>
            <form className="proposal-service-picker" onSubmit={searchLaborProducts}>
              <div className="filter-control">
                <label className="form-label" htmlFor="proposal-labor-search">Serviço</label>
                <input
                  id="proposal-labor-search"
                  className="form-control"
                  value={laborSearch}
                  onChange={(event) => setLaborSearch(event.target.value)}
                  placeholder="Buscar mão de obra ou serviço"
                />
              </div>
              <button type="submit" className="btn btn-outline-secondary" disabled={actionId === 'labor-search'}>{actionId === 'labor-search' ? 'Buscando...' : 'Buscar serviços'}</button>
            </form>
            <div className="proposal-service-results mt-2">
              {laborOptions.map((product) => {
                const key = productKey(product);
                const selected = selectedLaborItems.some((item) => productKey(item) === key);
                return (
                  <button key={key} type="button" className={`proposal-service-option ${selected ? 'active' : ''}`} onClick={() => toggleLaborItem(product)}>
                    <strong>{formatValue(productName(product))}</strong>
                    <span>{formatValue(product.sku || product.code, 'Sem código')} · {formatValue(product.unit, 'Sem unidade')} · {formatMoney(product.salePrice)}</span>
                  </button>
                );
              })}
              {!laborOptions.length && <span className="text-muted">Busque por “serviço”, “mão de obra” ou parte do nome do cadastro mestre.</span>}
            </div>
            {!!selectedLaborItems.length && (
              <div className="proposal-service-selected mt-2">
                {selectedLaborItems.map((item) => (
                  <button key={productKey(item)} type="button" onClick={() => toggleLaborItem(item)}>
                    {formatValue(productName(item))} · {formatMoney(item.salePrice)} ×
                  </button>
                ))}
              </div>
            )}
            <div className="alert alert-warning mt-3 mb-0">Mão de obra selecionada entra no totalizador da prévia. A inclusão como novo item no Bling ainda depende do endpoint de adicionar item à proposta.</div>
          </div>

          {preview && (
            <div className="client-detail-box mt-3">
              <h3>Prévia da atualização</h3>
              <p className="text-muted mb-2">Proposta {formatValue(selectedProposal.numero || selectedProposal.id)} · {formatValue(selectedProposal.cliente)}</p>
              <div className="client-table-wrap">
                <table className="client-audit-table">
                  <thead><tr><th>Item</th><th>Produto</th><th>Qtd.</th><th>Unid.</th><th>Unit. original</th><th>Novo unit.</th><th>Total novo</th><th>Custo unit. novo</th><th>Operação</th><th>Diferença total</th></tr></thead>
                  <tbody>
                    {preview.itens?.map((item) => {
                      const analysisItem = analysisItemsById.get(String(item.itemPropostaId)) || {};
                      const quantity = previewQuantity(analysisItem);
                      const originalUnitPrice = previewOriginalUnitPrice(analysisItem);
                      const finalUnitPrice = previewFinalUnitPrice(item, analysisItem);
                      const originalTotal = quantity * originalUnitPrice;
                      const finalTotal = quantity * finalUnitPrice;
                      const totalDiff = finalTotal - originalTotal;
                      const totalDiffPercent = originalTotal ? (totalDiff / originalTotal) * 100 : 0;
                      return (
                        <tr key={item.itemPropostaId}>
                          <td>{formatValue(analysisItem.codigo || item.itemPropostaId)}</td>
                          <td>{formatValue(analysisItem.descricao || item.descricao || item.itemPropostaId)}<br /><span className="text-muted">{formatValue(analysisItem.produtoIdBling, 'Sem ID Bling')}</span></td>
                          <td>{formatNumber(quantity)}</td>
                          <td>{formatValue(analysisItem.unidade)}</td>
                          <td>{formatMoney(originalUnitPrice)}</td>
                          <td>{formatMoney(finalUnitPrice)}{item.precoVenda === undefined && <br />} {item.precoVenda === undefined && <span className="text-muted">mantido</span>}</td>
                          <td><strong>{formatMoney(finalTotal)}</strong></td>
                          <td>{formatMoney(item.novoCusto)}</td>
                          <td>{formatValue(item.operacao)}</td>
                          <td>{formatMoney(totalDiff)} ({formatValue(totalDiffPercent.toFixed(2), '0')}%)</td>
                        </tr>
                      );
                    })}
                    {selectedLaborItems.map((item) => (
                      <tr key={`labor-${productKey(item)}`}>
                        <td>Serviço</td>
                        <td>{formatValue(productName(item))}<br /><span className="text-muted">{formatValue(item.refs?.blingProdutoId, 'Sem ID Bling')}</span></td>
                        <td>1</td>
                        <td>{formatValue(item.unit)}</td>
                        <td>Não aplicado</td>
                        <td>{formatMoney(item.salePrice)}</td>
                        <td><strong>{formatMoney(item.salePrice)}</strong></td>
                        <td>Não aplicado</td>
                        <td>MÃO_DE_OBRA</td>
                        <td>Não aplicado</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan="4">Totais</th>
                      <th>{formatMoney(previewTotals.materialOriginalSale)}</th>
                      <th>{previewTotals.itemCount} itens + {previewTotals.laborCount} serviços</th>
                      <th>{formatMoney(previewTotals.totalSale)}</th>
                      <th>{formatMoney(previewTotals.materialAverageUnitCost)}</th>
                      <th>{formatMoney(previewTotals.laborSale)} mão de obra</th>
                      <th>{formatMoney(previewTotals.materialSale - previewTotals.materialOriginalSale)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="alert alert-warning mt-3 mb-0">Custo interno no item da proposta não foi confirmado como atualizável no Bling. Itens “somente custo interno” não são enviados ao Bling.</div>
              <button type="button" className="btn btn-success mt-3" disabled={actionId === 'confirm-update'} onClick={() => confirmUpdate(false)}>Confirmar atualização seletiva</button>
            </div>
          )}

          {updateResult && (
            <div className={`alert mt-3 ${updateConflicts.length || updateResult.error ? 'alert-warning' : 'alert-success'}`}>
              {updateResult.error || 'Atualização solicitada ao integrador.'}
              {updateConflicts.length > 0 && (
                <>
                  <div className="client-table-wrap mt-3">
                    <table className="client-audit-table">
                      <thead><tr><th>Item</th><th>Produto</th><th>Campo</th><th>Sincronizado</th><th>Atual no Bling</th><th>Pretendido</th></tr></thead>
                      <tbody>
                        {updateConflicts.map((conflict) => {
                          const analysisItem = analysisItemsById.get(String(conflict.itemPropostaId)) || {};
                          return (
                            <tr key={`${conflict.itemPropostaId}-${conflict.campo}`}>
                              <td>{formatValue(analysisItem.codigo || conflict.itemPropostaId)}</td>
                              <td>{formatValue(analysisItem.descricao || conflict.itemPropostaId)}</td>
                              <td>{formatValue(conflict.campo)}</td>
                              <td>{formatMoney(conflict.valorSincronizado)}</td>
                              <td>{formatMoney(conflict.valorAtualBling)}</td>
                              <td>{formatMoney(conflict.valorPretendido)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" className="btn btn-warning mt-3" disabled={actionId === 'confirm-update'} onClick={() => confirmUpdate(true)}>
                    Confirmar conflito e atualizar mesmo assim
                  </button>
                </>
              )}
            </div>
          )}

          {(purchasePreparation || purchaseOrders.length > 0 || purchaseResult) && (
            <div className="client-detail-box mt-3">
              <h3>Fechamento da compra</h3>
              {purchasePreparation && (
                <>
                  <div className="proposal-summary-grid mb-3">
                    <div><strong>{purchasePreparation.summary?.itemCount || 0}</strong><span>itens</span></div>
                    <div><strong>{purchasePreparation.summary?.supplierCount || 0}</strong><span>fornecedores</span></div>
                    <div><strong>{purchasePreparation.summary?.itemsWithoutSelectedCost || 0}</strong><span>sem custo</span></div>
                    <div><strong>{purchasePreparation.summary?.itemsWithWarnings || 0}</strong><span>com alerta</span></div>
                  </div>
                  <div className="client-table-wrap">
                    <table className="client-audit-table">
                      <thead><tr><th>Item</th><th>Qtd. aprovada</th><th>Custo proposta</th><th>Fornecedor da compra</th><th>Preço compra</th><th>Diferença</th><th>Alertas</th></tr></thead>
                      <tbody>
                        {(purchasePreparation.items || []).map((item) => {
                          const selection = purchaseSelections[item.proposalItemId] || {};
                          const purchaseUnitValue = numericValue(selection.purchaseUnitValue);
                          const difference = purchaseUnitValue - numericValue(item.proposalCostUnitValue);
                          const totalDifference = difference * numericValue(selection.quantity || item.approvedQuantity);
                          return (
                            <tr key={item.proposalItemId}>
                              <td>{formatValue(item.code || item.proposalItemId)}<br /><span className="text-muted">{formatValue(item.description)}</span></td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  type="number"
                                  min="0"
                                  step="0.001"
                                  value={selection.quantity ?? item.approvedQuantity}
                                  onChange={(event) => updatePurchaseSelection(item.proposalItemId, { quantity: event.target.value })}
                                />
                                <span className="text-muted">{formatValue(item.unit)}</span>
                              </td>
                              <td>{formatMoney(item.proposalCostUnitValue)}<br /><span className="text-muted">{formatValue(item.proposalSupplierName, item.proposalSupplierId || 'Sem fornecedor')}</span></td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={selection.supplierBlingId || ''}
                                  onChange={(event) => {
                                    const quote = (item.availableQuotes || []).find((row) => String(row.fornecedor?.id || row.fornecedorIdBling || row.supplierId || '') === event.target.value) || {};
                                    updatePurchaseSelection(item.proposalItemId, {
                                      supplierBlingId: event.target.value,
                                      supplierName: quote.fornecedor?.nome || quote.fornecedorNome || quote.supplierName || event.target.value,
                                      quoteItemReferenceId: quote.itemPedidoCompraId || '',
                                      purchaseUnitValue: numericValue(quote.valorUnitario, selection.purchaseUnitValue)
                                    });
                                  }}
                                >
                                  <option value="">Selecionar fornecedor</option>
                                  {(item.availableQuotes || []).map((quote, index) => {
                                    const quoteSupplierId = String(quote.fornecedor?.id || quote.fornecedorIdBling || quote.supplierId || '');
                                    return (
                                      <option key={`${item.proposalItemId}-${quoteSupplierId}-${index}`} value={quoteSupplierId}>
                                        {formatValue(quote.fornecedor?.nome || quote.fornecedorNome || quoteSupplierId)} · {formatMoney(quote.valorUnitario)}
                                      </option>
                                    );
                                  })}
                                </select>
                              </td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={selection.purchaseUnitValue ?? 0}
                                  onChange={(event) => updatePurchaseSelection(item.proposalItemId, { purchaseUnitValue: event.target.value })}
                                />
                              </td>
                              <td>{formatMoney(difference)}<br /><span className="text-muted">Total {formatMoney(totalDifference)}</span></td>
                              <td>{item.warnings?.length ? item.warnings.join(' ') : 'Sem divergências'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div className="proposal-cost-actions mt-3">
                <button type="button" className="btn btn-success" disabled={!purchasePreparation?.items?.length || actionId === 'generate-purchase-orders'} onClick={generatePurchaseOrders}>
                  {actionId === 'generate-purchase-orders' ? 'Gerando...' : 'Gerar pedidos por fornecedor'}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled={actionId === 'purchase-preparation'} onClick={loadPurchasePreparation}>Atualizar revisão</button>
              </div>
              {!!purchaseOrders.length && (
                <div className="client-table-wrap mt-3">
                  <table className="client-audit-table">
                    <thead><tr><th>Fornecedor</th><th>Status</th><th>Itens</th><th>Subtotal</th><th>Total</th><th>Bling</th><th></th></tr></thead>
                    <tbody>
                      {purchaseOrders.map((order) => {
                        const orderId = order._id || order.id;
                        return (
                          <tr key={orderId}>
                            <td>{formatValue(order.supplierName, order.supplierBlingId)}</td>
                            <td>{formatValue(order.status)}</td>
                            <td>{order.items?.length || 0}</td>
                            <td>{formatMoney(order.subtotal)}</td>
                            <td><strong>{formatMoney(order.total)}</strong></td>
                            <td>{formatValue(order.blingPurchaseOrderNumber || order.blingPurchaseOrderId, 'Ainda não enviado')}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                disabled={actionId === `send-purchase-${orderId}` || order.status === 'CRIADO_NO_BLING'}
                                onClick={() => sendPurchaseOrder(order)}
                              >
                                {order.status === 'ERRO_DE_INTEGRACAO' ? 'Reprocessar' : 'Enviar ao Bling'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {purchaseResult && (
                <div className={`alert mt-3 mb-0 ${purchaseResult.ok === false ? 'alert-warning' : 'alert-success'}`}>
                  {purchaseResult.ok === false ? 'Ação concluída com falhas. Verifique os pedidos em erro.' : 'Ação de compra concluída.'}
                </div>
              )}
            </div>
          )}

          {audit && (
            <div className="client-detail-box mt-3">
              <h3>Auditoria</h3>
              <div className="client-table-wrap">
                <table className="client-audit-table">
                  <thead><tr><th>Data</th><th>Usuário</th><th>Tipo</th><th>Status</th><th>Pedido</th><th>Fornecedor</th></tr></thead>
                  <tbody>
                    {!(audit.rows || []).length && <tr><td colSpan="6">Nenhum registro de auditoria retornado pelo integrador.</td></tr>}
                    {(audit.rows || []).map((row) => (
                      <tr key={row._id || `${row.tipo}-${row.createdAt}`}><td>{formatDate(row.createdAt)}</td><td>{formatValue(row.usuarioId)}</td><td>{formatValue(row.tipo)}</td><td>{formatValue(row.status)}</td><td>{formatValue(row.pedidoCompraIdBling)}</td><td>{formatValue(row.fornecedorIdBling)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
